import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'

const stateStore = new WebStorageStateStore({ store: window.sessionStorage, prefix: 'crm.oidc.' })
export const userManager = new UserManager({
  authority: import.meta.env.VITE_OIDC_AUTHORITY ?? 'http://localhost:8081/realms/crm',
  client_id: import.meta.env.VITE_OIDC_CLIENT_ID ?? 'crm-web',
  redirect_uri: `${window.location.origin}/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: 'openid profile email',
  ui_locales: 'ru',
  stateStore,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  automaticSilentRenew: true,
  silent_redirect_uri: `${window.location.origin}/silent-renew`,
  accessTokenExpiringNotificationTimeInSeconds: 60,
  loadUserInfo: false,
})

export async function currentUser(): Promise<User | null> {
  const user = await userManager.getUser()
  return user && !user.expired ? user : null
}

let loginPending: Promise<void> | undefined
let callback: { url: string; result: Promise<string> } | undefined

export function safeReturnTo(value: unknown): string {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    && !value.includes(String.fromCharCode(92)) && !/^\/(callback|silent-renew)([/?#]|$)/.test(value)
    ? value : '/'
}

/** Concurrent API failures must start only one authorization request per tab. */
export function login(): Promise<void> {
  if (!loginPending) {
    loginPending = (async () => {
      await userManager.clearStaleState()
      await userManager.signinRedirect({
        state: { returnTo: safeReturnTo(window.location.pathname + window.location.search) },
      })
    })().catch((error: unknown) => { loginPending = undefined; throw error })
  }
  return loginPending
}

/** The code is one-use; React StrictMode can mount the callback twice. */
export function completeLogin(): Promise<string> {
  const url = window.location.href
  if (callback?.url === url) return callback.result
  const result = userManager.signinRedirectCallback(url).then((user) => {
    const state = user.state as { returnTo?: string } | undefined
    return safeReturnTo(state?.returnTo)
  })
  callback = { url, result }
  return result
}

/** Clear only OIDC data, preserving the user's other tab preferences. */
export async function restartLogin(): Promise<void> {
  userManager.stopSilentRenew()
  await userManager.removeUser()
  for (const key of await stateStore.getAllKeys()) await stateStore.remove(key)
  await userManager.clearStaleState()
  callback = undefined
  loginPending = undefined
  window.history.replaceState(null, '', '/')
  await login()
}

export function loginErrorKey(error: unknown): string {
  const message = error instanceof Error ? error.message : ''
  if (/state|matching/i.test(message)) return 'auth.stateExpired'
  if (/access_denied|cancel/i.test(message)) return 'auth.cancelled'
  if (/fetch|network|timeout/i.test(message)) return 'auth.networkError'
  return 'auth.exchangeFailed'
}

export async function logout(): Promise<void> {
  // Before the token is removed: the server forgets this browser's push subscription while
  // the request can still be authorised, and the browser drops it — the next person at this
  // computer must not receive the previous one's notifications (#120). Imported lazily: the
  // push module depends on the API client, which depends on this one.
  const { forgetBrowserPush } = await import('@/notifications/browserPush')
  await forgetBrowserPush()
  const user = await userManager.getUser()
  userManager.stopSilentRenew()
  await userManager.removeUser()
  await userManager.signoutRedirect({ id_token_hint: user?.id_token })
}

export async function accessToken(): Promise<string | null> {
  return (await currentUser())?.access_token ?? null
}
